// ImageUploader — Iter 40. Drop-in image uploader for admin editors.
// Renders an "Upload image" button next to the URL input. On upload it
// POSTs multipart/form-data to /api/admin/uploads/image, receives a
// stable /api/uploads/<uuid>.<ext> URL, and calls `onChange(url)` so the
// parent form can persist it directly into a SiteSettings string field.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { t } from "../../lib/i18n";

const MAX_BYTES = 6 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export default function ImageUploader({ onChange, testid = "image-uploader", label }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error(t.uploadInvalidType || "Only PNG, JPG, WEBP, GIF allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t.uploadTooLarge || "Image too large (max 6 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.url) throw new Error("No URL in response");
      onChange(data.url);
      toast.success(t.uploaded || "Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED.join(",")}
        onChange={handleFile}
        data-testid={`${testid}-file`}
        className="hidden"
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        data-testid={testid}
        title={label || t.uploadImage}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-[10px] uppercase tracking-[0.25em] font-bold disabled:opacity-40"
      >
        {busy ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} />}
        {busy ? (t.uploading || "Uploading...") : (label || t.uploadImage || "Upload")}
      </button>
    </>
  );
}
